import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api, { authApi } from '../services/api'
import {
  clearSession,
  consumeSessionNotice,
  getStoredToken,
  getStoredTokenExpiry,
  notifySessionEnded,
  onSessionEnded,
  refreshAccessToken,
  sessionEndedMessage,
  storeSession,
} from '../services/authSession'

// Durée de validité du cache d'authentification (5 minutes)
const AUTH_CACHE_DURATION = 5 * 60 * 1000

export interface UserRole {
  id: number
  name: string
  display_name: string
}

export interface User {
  id: number
  name: string
  phone: string
  email?: string
  role?: string
  avatar?: string
  wallet_balance: number
  is_active: boolean
  created_at: string
  roles?: Array<string | UserRole>
  permissions?: string[]
  /** Accès back-office (espace d'administration) — décidé par le backend (User::isBackOffice). */
  is_backoffice?: boolean
}

/**
 * Extrait la liste des noms de rôles d'un utilisateur, quelle que soit la forme
 * renvoyée par l'API :
 *  - `/auth/login` et `/me` → `roles: ['superadmin']` (tableau de chaînes, UserResource) ;
 *  - `/me/permissions` → `roles: [{ id, name, display_name }]` (objets).
 */
function userRoleNames(user: Pick<User, 'role' | 'roles'> | null): string[] {
  if (!user) return []

  const fromRoles = (user.roles ?? [])
    .map((role) => typeof role === 'string' ? role : role.name)
    .filter((name) => typeof name === 'string' && name.length > 0)

  if (typeof user.role === 'string' && user.role.length > 0) {
    return [user.role, ...fromRoles]
  }

  return fromRoles
}

/**
 * L'utilisateur a-t-il accès à l'espace d'administration (back-office) ?
 *
 * La décision appartient au backend (User::isBackOffice) : il suffit qu'un des
 * rôles de l'utilisateur soit un rôle back-office (tout rôle qui n'est pas
 * `client`/`driver`). Le frontend ne maintient AUCUNE liste de rôles.
 */
export function isStaffUser(user: Pick<User, 'role' | 'roles' | 'is_backoffice'> | null): boolean {
  if (!user) return false

  // Source de vérité : le flag calculé par le backend.
  if (typeof user.is_backoffice === 'boolean') {
    return user.is_backoffice
  }

  // Repli pour les sessions déjà ouvertes avant le déploiement de ce champ :
  // on ne duplique pas la liste des rôles back-office — on se limite à rejeter
  // les seuls rôles mobiles connus (client/chauffeur).
  const names = userRoleNames(user)
  return names.some((name) => name !== 'client' && name !== 'driver')
}

export function isSuperAdminUser(user: Pick<User, 'role' | 'roles'> | null): boolean {
  return userRoleNames(user).includes('superadmin')
}

export function hasSystemRole(user: Pick<User, 'role' | 'roles'> | null, role: string): boolean {
  return userRoleNames(user).includes(role)
}

// ─── Rotation proactive du jeton ────────────────────────────────────
//
// Le backend émet des jetons à durée de vie limitée (7 jours par défaut) et
// révoque l'ancien à chaque rotation (`POST /auth/refresh`). On renouvelle donc
// le jeton avant son échéance — au plus tard 24 h avant, et plutôt à mi-parcours
// du dernier quart restant — afin que l'utilisateur ne voit jamais de 401.

/** Avance maximale sur l'échéance (24 h) avant renouvellement. */
const ROTATION_MAX_LEAD_MS = 24 * 60 * 60 * 1000

/** Délai plancher entre deux tentatives (évite une boucle serrée). */
const ROTATION_MIN_DELAY_MS = 60 * 1000

/** Nouvelle tentative après une panne réseau transitoire (5 min). */
const ROTATION_RETRY_DELAY_MS = 5 * 60 * 1000

let rotationTimer: ReturnType<typeof setTimeout> | null = null

/** Nombre de millisecondes restantes avant l'échéance (null si inconnue). */
function remainingSessionMs(): number | null {
  const expiresAt = getStoredTokenExpiry()
  if (!expiresAt) return null

  const remaining = new Date(expiresAt).getTime() - Date.now()

  return Number.isFinite(remaining) ? remaining : null
}

function stopTokenRotation(): void {
  if (rotationTimer) {
    clearTimeout(rotationTimer)
    rotationTimer = null
  }
}

function scheduleTokenRotation(delayMs?: number): void {
  stopTokenRotation()

  const delay = delayMs ?? rotationDelay()

  if (delay === null) return

  rotationTimer = setTimeout(() => {
    void rotateToken()
  }, delay)
}

/** Délai avant rotation : un quart du temps restant, plafonné à 24 h. */
function rotationDelay(): number | null {
  const remaining = remainingSessionMs()

  if (remaining === null) return null

  const lead = Math.min(remaining / 4, ROTATION_MAX_LEAD_MS)

  return Math.max(remaining - lead, ROTATION_MIN_DELAY_MS)
}

/**
 * Renouvelle le jeton en arrière-plan. Tant que l'échéance n'est pas dépassée,
 * une panne réseau n'entraîne aucune déconnexion : on retente plus tard. Un 401
 * ou 403 signifie en revanche que le jeton est révoqué (rotation concurrente,
 * compte suspendu) : la session est close.
 */
async function rotateToken(): Promise<void> {
  try {
    const token = await refreshAccessToken()

    useAuthStore.setState({
      token,
      tokenExpiresAt: getStoredTokenExpiry(),
      isAuthenticated: true,
      lastValidated: Date.now(),
    })

    scheduleTokenRotation()
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status

    if (status === 401 || status === 403) {
      notifySessionEnded('expired')

      return
    }

    scheduleTokenRotation(ROTATION_RETRY_DELAY_MS)
  }
}

/**
 * Rotation immédiate si l'échéance approche : au retour sur l'onglet, car les
 * minuteurs des onglets en arrière-plan sont fortement ralentis par le
 * navigateur — le minuteur seul ne suffit pas à garantir la continuité.
 */
function rotateTokenIfNeeded(): void {
  const remaining = remainingSessionMs()

  if (remaining === null || !getStoredToken()) return

  if (remaining <= ROTATION_MAX_LEAD_MS) {
    void rotateToken()
  }
}

interface AuthState {
  user: User | null
  token: string | null
  /** Échéance ISO 8601 du jeton courant (fournie par le backend à l'émission). */
  tokenExpiresAt: string | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
  error: string | null
  permissions: string[]
  isSuperAdmin: boolean
  lastValidated: number | null // Timestamp de dernière validation

  // Actions
  login: (phone: string, password: string) => Promise<void>
  logout: () => void
  /** Révoque tous les jetons de l'utilisateur (tous ses appareils). */
  logoutAllSessions: () => Promise<void>
  checkAuth: () => Promise<void>
  fetchPermissions: () => Promise<void>
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
  hasRole: (role: string) => boolean
  shouldRevalidate: () => boolean
  clearError: () => void
  homePath: () => string
}

/** État d'une session fermée (déconnexion, expiration, compte suspendu). */
const EMPTY_SESSION = {
  user: null,
  token: null,
  tokenExpiresAt: null,
  isAuthenticated: false,
  permissions: [],
  isSuperAdmin: false,
  lastValidated: null,
} satisfies Partial<AuthState>

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      ...EMPTY_SESSION,
      isLoading: false,
      isInitialized: false,
      // Motif éventuel d'une fin de session (expiration, compte désactivé)
      // consommé une seule fois, après le rechargement de la page de connexion.
      error: consumeSessionNotice(),

      login: async (phone: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.login(phone, password)
          const { user, token, expires_at: expiresAt } = response.data.data

          // Vérifier que l'utilisateur fait partie du back-office (rôles admin)
          // NB : l'API renvoie `roles` (tableau de noms), pas un champ `role` seul.
          if (!isStaffUser(user)) {
            throw new Error('Accès réservé aux administrateurs')
          }

          // Jeton + échéance : stockage partagé avec les intercepteurs HTTP
          // (services/authSession) — la rotation en dépend.
          storeSession(token, expiresAt ?? null)
          
          // Mettre à jour l'état immédiatement (pas d'attente)
          set({
            user,
            token,
            tokenExpiresAt: expiresAt ?? null,
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            error: null,
            isSuperAdmin: isSuperAdminUser(user),
            lastValidated: Date.now(),
          })

          // Charger les permissions AVANT de résoudre le login : la navigation
          // (homePath, sidebar, gardes de route) est calculée à partir de cette
          // liste explicite ; attendre évite un onglet d'accueil erroné.
          await get().fetchPermissions()

          // Renouvellement du jeton programmé avant son échéance.
          scheduleTokenRotation()
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? error.message
              : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Erreur de connexion'
          set({ isLoading: false, error: message })
          throw error
        }
      },

      // Déconnexion locale : instantanée du point de vue de l'interface.
      logout: () => {
        const token = getStoredToken()

        stopTokenRotation()

        // Révocation serveur AVANT purge du stockage, et avec le jeton explicite :
        // sinon la requête partirait sans en-tête Authorization et le jeton
        // resterait utilisable côté backend jusqu'à son échéance (7 jours).
        if (token) {
          void api
            .post('/logout', null, { headers: { Authorization: `Bearer ${token}` } })
            .catch(() => {
              // Réseau indisponible : la session locale reste fermée.
            })
        }
        clearSession()
        set({ ...EMPTY_SESSION, isInitialized: true, error: null })
      },

      // Déconnexion de toutes les sessions (tous les appareils) : le backend
      // révoque l'intégralité des jetons de l'utilisateur.
      logoutAllSessions: async () => {
        stopTokenRotation()

        try {
          await authApi.logoutAll()
        } catch {
          // Jeton déjà révoqué ou backend injoignable : la session locale est
          // fermée malgré tout (aucun jeton exploitable ne doit subsister).
        }

        clearSession()
        set({ ...EMPTY_SESSION, isInitialized: true, error: null })
      },

      checkAuth: async () => {
        const token = getStoredToken()
        const { isAuthenticated, lastValidated } = get()
        
        // Si pas de token, marquer comme non authentifié
        if (!token) {
          set({ isAuthenticated: false, user: null, token: null, isInitialized: true, lastValidated: null })
          return
        }

        // Si déjà authentifié et cache encore valide, ne pas revalider
        if (isAuthenticated && lastValidated && (Date.now() - lastValidated) < AUTH_CACHE_DURATION) {
          set({ isInitialized: true })
          return
        }

        set({ isLoading: true })
        try {
          const response = await api.get('/me')
          const user = response.data.data

          if (!isStaffUser(user)) {
            throw new Error('Accès non autorisé')
          }

          set({
            user,
            token,
            tokenExpiresAt: getStoredTokenExpiry(),
            isAuthenticated: true,
            isLoading: false,
            isInitialized: true,
            isSuperAdmin: isSuperAdminUser(user),
            lastValidated: Date.now(),
          })

          // Charger les permissions (bloquant : la sidebar et les gardes de
          // route en dépendent directement).
          await get().fetchPermissions()

          // Le minuteur de rotation ne survit pas à un rechargement de page :
          // on le reprogramme à partir de l'échéance stockée du jeton.
          scheduleTokenRotation()
        } catch {
          stopTokenRotation()
          clearSession()
          set({ ...EMPTY_SESSION, isLoading: false, isInitialized: true })
        }
      },

      // Vérifie si une revalidation est nécessaire
      shouldRevalidate: () => {
        const { lastValidated, isAuthenticated } = get()
        if (!isAuthenticated) return true
        if (!lastValidated) return true
        return (Date.now() - lastValidated) >= AUTH_CACHE_DURATION
      },

      fetchPermissions: async () => {
        try {
          const response = await authApi.myPermissions()
          const { permissions, is_superadmin, roles } = response.data.data
          
          set((state) => ({
            permissions,
            isSuperAdmin: is_superadmin,
            user: state.user ? { ...state.user, roles, permissions } : null,
          }))
        } catch (error) {
          console.error('Erreur lors du chargement des permissions:', error)
        }
      },

      // NB : plus aucun contournement pour le superadmin. Depuis la
      // restriction du rôle superadmin (cf. RolesAndPermissionsSeeder),
      // la liste explicite renvoyée par /me/permissions fait foi.
      hasPermission: (permission: string) => {
        const { permissions } = get()
        return permissions.includes(permission)
      },

      hasAnyPermission: (perms: string[]) => {
        const { permissions } = get()
        return perms.some(p => permissions.includes(p))
      },

      hasAllPermissions: (perms: string[]) => {
        const { permissions } = get()
        return perms.every(p => permissions.includes(p))
      },

      hasRole: (role: string) => {
        const { user } = get()
        return hasSystemRole(user, role)
      },

      // Page d'accueil après connexion : le premier onglet accessible du
      // back-office (le dashboard n'est pas accessible à tous les rôles).
      homePath: () => {
        const { hasPermission } = get()

        // Premier onglet réellement accessible selon les permissions
        // accordées par le backend.
        if (hasPermission('dashboard.view')) return '/'
        if (hasPermission('users.view')) return '/staff'
        if (hasPermission('rides.view')) return '/rides'
        if (hasPermission('drivers.view')) return '/drivers'
        if (hasPermission('vehicle_types.view')) return '/vehicle-types'
        if (hasPermission('flash_infos.view')) return '/flash-infos'
        if (hasPermission('finance.view')) return '/finance'
        if (hasPermission('notifications.view')) return '/notifications'
        if (hasPermission('audit.view')) return '/audit'
        if (hasPermission('roles.view')) return '/roles'
        if (hasPermission('permissions.view')) return '/permissions'
        // Aucune permission domaine : le profil reste toujours accessible.
        return '/profile'
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'goflash-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        tokenExpiresAt: state.tokenExpiresAt,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
        isSuperAdmin: state.isSuperAdmin,
      }),
    }
  )
)

// ─── Cycle de vie de la session ─────────────────────────────────────

// Fin de session décidée par la couche HTTP : jeton expiré sans rotation
// possible, ou compte suspendu côté backend. L'état est vidé puis l'utilisateur
// est ramené à l'écran de connexion, où le motif de fermeture l'attend.
onSessionEnded((reason) => {
  stopTokenRotation()

  useAuthStore.setState({
    ...EMPTY_SESSION,
    isInitialized: true,
    error: sessionEndedMessage(reason),
  })

  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login')
  }
})

// Retour sur l'onglet : le minuteur de rotation peut avoir été ralenti par le
// navigateur (onglet en arrière-plan) ; on revérifie alors l'échéance du jeton.
if (typeof window !== 'undefined') {
  window.addEventListener('focus', rotateTokenIfNeeded)
}
